import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { type ApiError, api, unwrap } from '../api/client';
import { Card, ErrorAlert, Field } from '../components/ui';

/** IAM-14 / UI-37: ask for a reset code; the answer never says whether the address exists. */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const m = useMutation({
    mutationFn: () => unwrap(api.post('/auth/password/forgot', { email })),
  });
  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <div className="brand" style={{ padding: 0, marginBottom: 12 }}>
          <span className="brand-mark">A</span> AutoDM
        </div>
        <h1>Forgot your password?</h1>
        {m.isSuccess ? (
          <>
            <p>
              If that address has an account, a six-digit code is on its way. It expires in 15
              minutes.
            </p>
            <Link className="btn btn-primary" to={`/reset?email=${encodeURIComponent(email)}`}>
              I have the code
            </Link>
          </>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              m.mutate();
            }}
          >
            <ErrorAlert error={m.error as ApiError | null} />
            <Field label="Email">
              <input
                className="input"
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <button className="btn btn-primary" type="submit" disabled={m.isPending}>
              Send me a code
            </button>
          </form>
        )}
        <p className="muted small" style={{ marginTop: 14 }}>
          Remembered it? <Link to="/login">Log in</Link>
        </p>
      </Card>
    </div>
  );
}

/** IAM-14 / UI-37: the mailed code sets a new password (first password for invited members too). */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: params.get('email') ?? '', code: '', password: '' });
  const m = useMutation({
    mutationFn: () => unwrap(api.post('/auth/password/reset', form)),
    onSuccess: () => navigate('/login', { state: { email: form.email, reset: true } }),
  });
  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <div className="brand" style={{ padding: 0, marginBottom: 12 }}>
          <span className="brand-mark">A</span> AutoDM
        </div>
        <h1>Set your password</h1>
        <p className="muted small">
          Enter the six-digit code from your email and choose a password.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            m.mutate();
          }}
        >
          <ErrorAlert error={m.error as ApiError | null} />
          <Field label="Email">
            <input
              className="input"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label="Code">
            <input
              className="input"
              inputMode="numeric"
              pattern="[0-9]{6}"
              name="code"
              autoComplete="one-time-code"
              required
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
          </Field>
          <Field label="New password" help="At least 8 characters">
            <input
              className="input"
              type="password"
              name="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </Field>
          <button className="btn btn-primary" type="submit" disabled={m.isPending}>
            Set password
          </button>
        </form>
        <p className="muted small" style={{ marginTop: 14 }}>
          No code yet? <Link to="/forgot">Request one</Link>
        </p>
      </Card>
    </div>
  );
}

/** IAM-14 / UI-37: the signed-in user changes their own password; this session stays, the others end. */
export function ChangePasswordCard() {
  const [form, setForm] = useState({ current: '', next: '' });
  const m = useMutation({
    mutationFn: () => unwrap(api.post('/auth/password', form)),
    onSuccess: () => setForm({ current: '', next: '' }),
  });
  return (
    <Card title="Change password">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorAlert error={m.error as ApiError | null} />
        {m.isSuccess ? (
          <div className="alert alert-info" role="status">
            Password changed. Every other session of yours has been signed out.
          </div>
        ) : null}
        <Field label="Current password">
          <input
            className="input"
            type="password"
            name="current"
            autoComplete="current-password"
            required
            value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })}
          />
        </Field>
        <Field label="New password" help="At least 8 characters">
          <input
            className="input"
            type="password"
            name="next"
            autoComplete="new-password"
            required
            minLength={8}
            value={form.next}
            onChange={(e) => setForm({ ...form, next: e.target.value })}
          />
        </Field>
        <button className="btn btn-primary" type="submit" disabled={m.isPending}>
          Change password
        </button>
      </form>
    </Card>
  );
}
